import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import { useWaitlist } from '@/context/WaitlistContext';
import { useAddToWaitlist, useCheckWaitlistStatus } from '@/hooks/api/useWaitlist';
import { useAnalytics } from '@/hooks/useAnalytics';
import { generateReferralUrl } from '@/utils/referralUtils';
import { useReferral } from '@/hooks/useReferral';
import WaitlistSuccessModal from './WaitlistSuccessModal';

interface WaitlistFormProps {
  variant?: 'default' | 'dark';
  className?: string;
}

const WaitlistForm: React.FC<WaitlistFormProps> = ({
  variant = 'default',
  className = ''
}) => {
  const [email, setEmail] = useState('');
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [referralCode, setReferralCode] = useState<string | undefined>();
  const [referralUrl, setReferralUrl] = useState<string | undefined>();
  const { showThankYou } = useWaitlist();
  const { toast } = useToast();
  const { trackWaitlistSignup, trackFormSubmission } = useAnalytics();
  const { referralCode: urlReferralCode } = useReferral();

  // React Query hooks
  const addToWaitlistMutation = useAddToWaitlist();
  const { data: statusData } = useCheckWaitlistStatus(email, false); // Disabled by default


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();


    if (!email || !email.includes('@')) {
      toast({
        title: "Invalid email",
        description: "Please enter a valid email address.",
        variant: "destructive",
      });
      return;
    }

    try {
      // Use the React Query mutation to add to waitlist
      const response = await addToWaitlistMutation.mutateAsync({
        email: email.trim(),
        referral_code: urlReferralCode,
      });

      // Store referral data for success modal
      setReferralCode(response.referral_code);

      // Generate referral URL on frontend
      if (response.referral_code) {
        const generatedReferralUrl = generateReferralUrl(response.referral_code);
        setReferralUrl(generatedReferralUrl);
      }

      // Track successful waitlist signup
      trackWaitlistSignup(email.trim());
      trackFormSubmission('waitlist', true);

      // Show success modal
      setShowSuccessModal(true);
      setEmail('');

    } catch (error) {

      // Track failed form submission
      trackFormSubmission('waitlist', false);

      // Error handling is already done in the mutation hook
      // which shows appropriate toast messages
    }
  };


  const inputClasses = variant === 'dark'
    ? "bg-white/10 border-white/20 text-white placeholder:text-white/70 focus-visible:border-white/50"
    : "bg-white border-[#DDDDDD] text-[#2C2C2C] placeholder:text-[#999999] focus-visible:border-[#6C4F77]";

  const buttonClasses = variant === 'dark'
    ? "bg-[#F4978E] text-white hover:bg-[#E3867A]"
    : "bg-[#6C4F77] text-white hover:bg-[#5A3A68]";

  return (
    <>
      <form onSubmit={handleSubmit} className={`flex flex-col sm:flex-row gap-3 w-full mx-auto ${className} md:max-w-md lg:max-w-lg`}>
        <Input
          type="email"
          placeholder="Enter your email to stay close"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={addToWaitlistMutation.isPending}
          className={`flex-1 ${inputClasses}`}
        />
        <Button
          type="submit"
          disabled={addToWaitlistMutation.isPending}
          className={`font-semibold px-6 ${buttonClasses} h-[58px] text-md`}
        >
          {addToWaitlistMutation.isPending ? 'Joining...' : 'Get early access'}
        </Button>
      </form>

      <WaitlistSuccessModal
        open={showSuccessModal}
        onOpenChange={setShowSuccessModal}
        isAlreadyOnWaitlist={false}
        referralCode={referralCode}
        referralUrl={referralUrl}
      />
    </>
  );
};

export default WaitlistForm;
